/* eslint-disable quotes */
import React, { useEffect, useState, } from "react";
import { Hero } from "components/generic/Hero";
import { ModalCentered } from "components/generic/ModalCentered";
import { Modal, Button } from "react-bootstrap";
import { assertNever } from "helpers/Helpers";
import { INewBookingPropsBase } from "types/INewBookingPropsBase";
import { scrollToTop } from "helpers/ScrollingHelper";
import { Form as FormikForm, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import { FormikFormRow } from "components/generic/FormRow";
import { OperatorVehicleDetails, OperatorVehicleResult, VehicleDetails, MicrositePageDetails, SiteTypeFilter } from "generated/types/types";
import { toast } from "react-toastify";
import { WarningOptions } from "constants/Toasts";
import { useTranslation } from "react-i18next";
import { ApiUrlResolver } from "utils/ApiUrlResolver";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import { LoadingBar } from "components/LoadingBar";

interface Props extends INewBookingPropsBase {
    micrositePin: string;
    currentVehicleDetails: VehicleDetails | null;
    setVehicleDetails: (vehicleDetails: VehicleDetails) => void;
}

type FormValues = {
    regNo: string;
};

export const VehicleLookup: React.FC<Props> = (props: Props) => {
    const initialValues: FormValues = {
        regNo: props.currentVehicleDetails?.registrationNumber?.toString() ?? "",
    };

    const [vehicleDetails, setVehicleDetails] = useState<OperatorVehicleResult>({ kind: "NotValidVehicle" });
    const [modalShow, setModalShow] = React.useState(false);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [micrositePageDetails, setMicrositePageDetails] = useState<MicrositePageDetails | null>(null);
    const [multiStepModalStep, setMultiStepModalStep] = useState<number>(1);
    const [searchInProgress, setSearchInProgress] = useState<boolean>(false);
    const { t, i18n } = useTranslation();
    const countryCode = useSelector((state: RootState) => state.country.countryCode);

    const companyName = micrositePageDetails?.micrositeSetting?.customCompanyName || '';
    const phoneNumber = micrositePageDetails?.micrositeContactDetails?.phoneNumber || '';
    const email = micrositePageDetails?.micrositeContactDetails?.email || '';

    const RevalidateOnLanguageChange: React.FC<{ lang: string }> = ({ lang }) => {
    const { validateForm } = useFormikContext<any>();

    React.useEffect(() => {
        validateForm();
    }, [lang, validateForm]);

    return null;
    };

    const schema = yup.object({
        regNo: yup.string().required(t('vehicle_details_invalid_reg')).max(15, t('vehicle_details_invalid_reg_characters')),
    });

    useEffect(() => scrollToTop(), []);

    const lookupVehicle = (regNo: string) => {
        setSearchInProgress(true);
        fetch(ApiUrlResolver.getWebApiUrl(countryCode) + `/Vehicle/GetDetails?micrositePin=${props.micrositePin}&vehicleRegNo=${regNo}`)
            .then(response => response.json())
            .then(data => {
                setVehicleDetails(data);
                loadDriverMicrositeDetails(props.micrositePin);
                setModalShow(true);
            }) 
            .finally(() => {
            setSearchInProgress(false);
        });
    };

    function loadDriverMicrositeDetails(micrositePin: string) {
        fetch(ApiUrlResolver.getSettingAPIUrl(countryCode) + `/DriverMicrosite/PageDetails/${micrositePin}`)
            .then(response => response.json())
            .then((data: MicrositePageDetails) => {
                setMicrositePageDetails(data);
            });
    }

    const handleRetry = () => {
        setActionsDisabled(false);
        setModalShow(false);
        setMultiStepModalStep(1);
    };

    const handleSubmit = (values: FormValues) => {
        setActionsDisabled(true);

        const currentRegistrationNumber: string | null = props.currentVehicleDetails?.registrationNumber ?? null;
        if (currentRegistrationNumber !== null && values.regNo !== currentRegistrationNumber) {
            toast.warning(t('vehicle_details_different_reg'), WarningOptions());
        }

        lookupVehicle(values.regNo);
    };

    const handleDetailsConfirmed = (operatorVehicleDetails: OperatorVehicleDetails, values: FormValues) => {
        const newVehicleDetails: VehicleDetails = {
            encryptedId: operatorVehicleDetails.encryptedId,
            scheme: operatorVehicleDetails.scheme,
            registrationNumber: values.regNo,
            make: operatorVehicleDetails.make,
            model: operatorVehicleDetails.model,
            derivative: operatorVehicleDetails.derivative,
            odoReading: props.currentVehicleDetails?.odoReading ?? "",
            motDueDate: operatorVehicleDetails.motDueDate,
            lastOdoReading: operatorVehicleDetails.lastOdoReading,
            safetyRecall: props.currentVehicleDetails?.safetyRecall ?? false,
            assetId: operatorVehicleDetails.assetId
        };
        props.setVehicleDetails(newVehicleDetails);

        if (micrositePageDetails?.micrositeSetting.duplicateBookingCheck && vehicleDetails.kind === "OperatorVehicleDetails" && vehicleDetails.hasFutureOpenBooking) {
            setMultiStepModalStep(2); // Move to the next step for future booking confirmation
        } else {
            setModalShow(false);
            props.goToNextStep();
        }

        const tempReg = sessionStorage.getItem("tempReg") || "";

        if (tempReg.includes(values.regNo)) {
            sessionStorage.setItem("prevReg", values.regNo);
        }

        if (!tempReg.includes(values.regNo)) {
            sessionStorage.setItem("tempReg", values.regNo);
            sessionStorage.setItem("tempPostCode", "");
            sessionStorage.setItem("isMainDealer", JSON.stringify(false));
            sessionStorage.setItem("siteName", "");
            sessionStorage.setItem("siteTypeRestriction", SiteTypeFilter.All);
            sessionStorage.setItem("searchRadius", "");
        }
    };

    const multiStepModalContents = (vehicleDetails: OperatorVehicleResult, values: FormValues) => {
        switch (multiStepModalStep) {
            case 1:
                switch (vehicleDetails.kind) {
                    case "OperatorVehicleDetails":
                        return (
                            <Modal.Body>
                                <h1>{t('vehicle_details_header')}</h1>
                                <h2>{t('vehicle_details_modal_description')}</h2>
                                <p>{values.regNo}</p>
                                <p>
                                    {vehicleDetails.make} {vehicleDetails.model}
                                </p>
                                <p>{vehicleDetails.derivative}</p>
                                <Button size="lg" onClick={() => handleDetailsConfirmed(vehicleDetails, values)}>
                                    {t('vehicle_details_modal_yes')}
                                </Button>
                                <p className="link-muted" onClick={handleRetry}>
                                    {t('vehicle_details_modal_no')}
                                </p>
                            </Modal.Body>
                        );
                    case "NotValidVehicle":
                        return (
                            <Modal.Body>
                                <h1>{t('vehicle_details_header')}</h1>
                                 <p>
                                    {t('vehicle_details_not_valid', {
                                    companyName,
                                    phoneNumber,
                                    email,
                                    })}
                                </p>
                                <Button size="lg" onClick={handleRetry}>
                                    {t('vehicle_details_not_valid_retry')}
                                </Button>
                            </Modal.Body>
                        );
                    default:
                        assertNever(vehicleDetails);
                }
                break;
            case 2:
                return (
                    <Modal.Body>
                        <div style={{ textAlign: "center", maxWidth: "400px", margin: "0 auto" }}>
                            <h1>{t('vehicle_details_existing_header')}</h1>
                            <h2 style={{ marginBottom: "10px" }}>{t('vehicle_details_existing_intro')}</h2>
                            <p>{t('vehicle_details_existing_description')}</p>
                        </div>
                        <div style={{ textAlign: "center" }}>
                            <Button
                                size="lg"
                                onClick={() => {
                                    setModalShow(false);
                                    props.goToNextStep();
                                }}
                            >
                                {t('vehicle_details_modal_yes')}
                            </Button>
                        </div>
                        <p className="link-muted" style={{ textAlign: "center", marginTop: "10px" }} onClick={handleRetry}>
                            {t("vehicle_details_existing_no")}
                        </p>
                    </Modal.Body>
                );
            default:
                return null;
        }
    };

    return (
        <div className="row no-gutters">
            <div className="col-12">
                <Hero heading="vehicle_details_header" description="vehicle_details_intro" />
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {({ values, handleChange }) => (
                        <div>
                            <FormikForm>
                                 <RevalidateOnLanguageChange lang={i18n.language} />
                                <div className="form-group">
                                    <FormikFormRow
                                        name="regNo"
                                        type="text"
                                        placeholder={t("vehicle_details_search_placeholder")}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            event.currentTarget.value = event.currentTarget.value.replace(/ /g, "");
                                            handleChange(event);
                                        }}
                                    />
                                </div>
                                <Button type="submit" variant="primary" className="large-input" disabled={actionsDisabled || searchInProgress}>
                                    {t("vehicle_details_search_button")}
                                </Button>
                                {searchInProgress && (
                                    <div className="mt-3">
                                        <LoadingBar />
                                    </div>
                                )}
                            </FormikForm>

                            <ModalCentered size="sm" show={modalShow} onHide={handleRetry}>
                                <div className="custom-dialog">
                                    <Modal.Header closeButton />
                                    {multiStepModalContents(vehicleDetails, values)}
                                </div>
                            </ModalCentered>
                        </div>
                    )}
                </Formik>
            </div>
        </div>
    );
};
